import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './Select.scss';

const DROPDOWN_MAX_HEIGHT = 240;
const DROPDOWN_OFFSET = 6;

function Select({
  label,
  options = [],
  value,
  onChange,
  placeholder = 'Vyber možnosť...',
  required = false,
  disabled = false,
  error,
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [portalStyle, setPortalStyle] = useState({ position: 'fixed', top: 0, left: 0, width: 0, zIndex: 9999 });
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const calculatePosition = () => {
    if (!selectRef.current) return;
    const rect = selectRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const shouldOpenUpward = spaceBelow < DROPDOWN_MAX_HEIGHT + DROPDOWN_OFFSET + 8;

    setOpenUpward(shouldOpenUpward);
    setPortalStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      ...(shouldOpenUpward
        ? { bottom: window.innerHeight - rect.top + DROPDOWN_OFFSET }
        : { top: rect.bottom + DROPDOWN_OFFSET }),
    });
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (
        selectRef.current && !selectRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Reposition on scroll / resize while open
  useEffect(() => {
    if (!isOpen) return;
    const update = () => calculatePosition();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) calculatePosition();
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (opt) => {
    if (opt.disabled) return;
    onChange?.(opt.value);
    setIsOpen(false);
  };

  return (
    <div className={`select ${className}`} ref={selectRef}>
      {label && <label className="select__label">{label}</label>}

      <div
        className={`select__control ${isOpen ? 'select__control--focused' : ''} ${
          disabled ? 'select__control--disabled' : ''
        } ${error ? 'select__control--error' : ''}`}
        onClick={handleToggle}
      >
        <span className={`select__value ${!selectedOption ? 'select__placeholder' : ''}`}>
          {/* Voliteľná farba možnosti — trasy sa všade inde poznávajú podľa
              nej, takže vo výbere musí byť tiež, inak si ju človek musí
              prekladať z názvu. */}
          {selectedOption?.color && (
            <span className="select__dot" style={{ backgroundColor: selectedOption.color }} />
          )}
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className={`select__arrow ${isOpen ? 'select__arrow--open' : ''}`}>▼</span>
      </div>

      {createPortal(
        <div
          ref={dropdownRef}
          className={`select__dropdown ${isOpen ? 'select__dropdown--open' : ''} ${
            openUpward ? 'select__dropdown--upward' : ''
          }`}
          style={portalStyle}
        >
          {options.map((opt, i) => (
            <button
              key={i}
              type="button"
              className={`select__option ${opt.value === value ? 'select__option--selected' : ''} ${
                opt.disabled ? 'select__option--disabled' : ''
              }`}
              onClick={() => handleSelect(opt)}
            >
              {opt.color && (
                <span className="select__dot" style={{ backgroundColor: opt.color }} />
              )}
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}

      {error && <span className="select__error">{error}</span>}
    </div>
  );
}

export default Select;
